import axios from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_URL || '/admin/api/v1'

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // Include cookies in all requests (needed for httpOnly refresh token)
  withCredentials: true,
})

// Token accessor — set by auth store after initialization to avoid circular dependency
let _getAccessToken: () => string | null = () => null
let _setAuthState: ((state: Record<string, unknown>) => void) | null = null

export function registerAuthAccessors(
  getToken: () => string | null,
  setState: (state: Record<string, unknown>) => void
) {
  _getAccessToken = getToken
  _setAuthState = setState
}

// Request interceptor to add auth token from in-memory store
api.interceptors.request.use(
  (config) => {
    const accessToken = _getAccessToken()
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle auth errors with automatic refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    // If 401 and not already retrying, attempt token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      try {
        // Refresh token is sent automatically as httpOnly cookie
        const response = await axios.post(
          `${API_BASE_URL}/auth/refresh`,
          {},
          { withCredentials: true }
        )
        const { accessToken: newAccessToken } = response.data

        // Update in-memory token
        if (_setAuthState) {
          _setAuthState({ accessToken: newAccessToken })
        }

        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
        return api(originalRequest)
      } catch (refreshError) {
        // Refresh failed, force logout
        if (_setAuthState) {
          _setAuthState({
            admin: null,
            accessToken: null,
            isAuthenticated: false,
          })
        }
        window.location.href = '/login'
        return Promise.reject(refreshError)
      }
    }

    return Promise.reject(error)
  }
)

// Auth API
export const authApi = {
  login: async (email: string, password: string) => {
    const response = await api.post('/auth/login', { email, password })
    return response.data
  },
  loginWithMfa: async (email: string, password: string, mfaCode: string) => {
    const response = await api.post('/auth/login', { email, password, mfaCode })
    return response.data
  },
  logout: async () => {
    const response = await api.post('/auth/logout')
    return response.data
  },
  me: async () => {
    const response = await api.get('/auth/me')
    return response.data
  },
  meWithToken: async (accessToken: string) => {
    const response = await axios.get(`${API_BASE_URL}/auth/me`, {
      headers: { Authorization: `Bearer ${accessToken}` },
      withCredentials: true,
    })
    return response.data
  },
  refresh: async () => {
    // Refresh token is sent automatically as httpOnly cookie
    const response = await axios.post(
      `${API_BASE_URL}/auth/refresh`,
      {},
      { withCredentials: true }
    )
    return response.data
  },
}

// Workspaces API
export const workspacesApi = {
  list: async (params?: {
    page?: number
    limit?: number
    search?: string
    /** A2 gap-port: server-side plan filter (STARTER | PROFESSIONAL | ENTERPRISE) */
    plan?: string
    /** A2 gap-port: server-side status filter (ACTIVE | SUSPENDED) */
    status?: string
  }) => {
    const response = await api.get('/workspaces', { params })
    return response.data
  },
  get: async (id: string) => {
    const response = await api.get(`/workspaces/${id}`)
    return response.data
  },
  getStats: async (id: string) => {
    const response = await api.get(`/workspaces/${id}/stats`)
    return response.data
  },
  suspend: async (id: string, reason: string) => {
    const response = await api.patch(`/workspaces/${id}/suspend`, { reason })
    return response.data
  },
  unsuspend: async (id: string) => {
    const response = await api.patch(`/workspaces/${id}/unsuspend`)
    return response.data
  },
  changePlan: async (id: string, plan: string, reason: string) => {
    const response = await api.patch(`/workspaces/${id}/plan`, { plan, reason })
    return response.data
  },
  bulkSuspend: async (ids: string[], reason: string) => {
    const response = await api.post('/workspaces/bulk/suspend', { ids, reason })
    return response.data
  },
  bulkUnsuspend: async (ids: string[]) => {
    const response = await api.post('/workspaces/bulk/unsuspend', { ids })
    return response.data
  },
  bulkExport: async (ids: string[]) => {
    const response = await api.post(
      '/workspaces/bulk/export',
      { ids },
      { responseType: 'blob' }
    )
    return response.data
  },
  // Feature Config endpoints
  getFeatures: async (id: string) => {
    const response = await api.get(`/workspaces/${id}/features`)
    return response.data
  },
  updateFeatures: async (id: string, featureFlags: Record<string, boolean>) => {
    const response = await api.put(`/workspaces/${id}/features`, featureFlags)
    return response.data
  },
  updateLimits: async (id: string, customLimits: Record<string, number>) => {
    const response = await api.put(`/workspaces/${id}/limits`, customLimits)
    return response.data
  },
  getUsage: async (id: string) => {
    const response = await api.get(`/workspaces/${id}/usage`)
    return response.data
  },
  /** A3 gap-port: create a new workspace (maps to AddTenantView flow) */
  create: async (data: {
    name: string
    ownerEmail: string
    ownerName: string
    plan?: string
  }) => {
    const response = await api.post('/workspaces', data)
    return response.data
  },
}

// Users API
export const usersApi = {
  list: async (params?: {
    page?: number
    limit?: number
    search?: string
    status?: string
    /** A4 gap-port: role filter (ADMIN | TENANT_OWNER | TENANT_USER) */
    role?: string
  }) => {
    const response = await api.get('/users', { params })
    return response.data
  },
  get: async (id: string) => {
    const response = await api.get(`/users/${id}`)
    return response.data
  },
  suspend: async (id: string, reason: string) => {
    const response = await api.post(`/users/${id}/suspend`, { reason })
    return response.data
  },
  unsuspend: async (id: string) => {
    const response = await api.post(`/users/${id}/unsuspend`)
    return response.data
  },
  impersonate: async (id: string, reason: string) => {
    const response = await api.post(`/users/${id}/impersonate`, { reason })
    return response.data
  },
  bulkSuspend: async (ids: string[], reason: string) => {
    const response = await api.post('/users/bulk/suspend', { ids, reason })
    return response.data
  },
  bulkExport: async (ids: string[]) => {
    const response = await api.post(
      '/users/bulk/export',
      { ids },
      { responseType: 'blob' }
    )
    return response.data
  },
}

// Audit API
export const auditApi = {
  list: async (params?: {
    page?: number
    limit?: number
    action?: string
    adminId?: string
    startDate?: string
    endDate?: string
  }) => {
    const response = await api.get('/audit', { params })
    return response.data
  },
  stats: async (params?: { startDate?: string; endDate?: string }) => {
    const response = await api.get('/audit/stats', { params })
    return response.data
  },
  export: async (params: {
    format: 'csv' | 'json'
    action?: string
    startDate?: string
    endDate?: string
    adminId?: string
    resourceType?: string
  }) => {
    const response = await api.get('/audit/export', {
      params,
      responseType: 'blob',
    })
    return response.data
  },
}

// Tenant Audit API (legacy audit_logs stream — distinct from platform admin audit)
export const tenantAuditApi = {
  list: async (params?: {
    page?: number
    limit?: number
    eventType?: string
    resourceType?: string
    tenantId?: string
    userId?: string
    severity?: string
    startDate?: string
    endDate?: string
  }) => {
    const response = await api.get('/audit/tenant-audit', { params })
    return response.data
  },
}

// Dashboard API
export const dashboardApi = {
  stats: async () => {
    const response = await api.get('/dashboard/stats')
    return response.data
  },
  activity: async (limit?: number) => {
    const response = await api.get('/dashboard/activity', { params: { limit } })
    return response.data
  },
  growth: async (days?: number) => {
    const response = await api.get('/dashboard/growth', { params: { days } })
    return response.data
  },
}

// Channels API
export const channelsApi = {
  list: async (params?: {
    page?: number
    limit?: number
    status?: string
    type?: string
    search?: string
  }) => {
    const response = await api.get('/channels', { params })
    return response.data
  },
  get: async (id: string) => {
    const response = await api.get(`/channels/${id}`)
    return response.data
  },
  stats: async () => {
    const response = await api.get('/channels/stats')
    return response.data
  },
  disconnect: async (id: string, reason: string) => {
    const response = await api.post(`/channels/${id}/disconnect`, { reason })
    return response.data
  },
  reconnect: async (id: string) => {
    const response = await api.post(`/channels/${id}/connect`)
    return response.data
  },
}

// Platform Admins API
export const adminsApi = {
  list: async (params?: { page?: number; limit?: number; status?: string }) => {
    const response = await api.get('/admins', { params })
    return response.data
  },
  get: async (id: string) => {
    const response = await api.get(`/admins/${id}`)
    return response.data
  },
  create: async (data: {
    email: string
    name: string
    password: string
    role: string
  }) => {
    const response = await api.post('/admins', data)
    return response.data
  },
  update: async (id: string, data: { name?: string; role?: string }) => {
    const response = await api.patch(`/admins/${id}`, data)
    return response.data
  },
  delete: async (id: string) => {
    const response = await api.delete(`/admins/${id}`)
    return response.data
  },
  resetPassword: async (id: string) => {
    const response = await api.post(`/admins/${id}/reset-password`)
    return response.data
  },
  unlock: async (id: string) => {
    const response = await api.post(`/admins/${id}/unlock`)
    return response.data
  },
}

// Billing API (admin)
export const billingApi = {
  getStats: async () => {
    const response = await api.get(`/billing/admin/stats`)
    return response.data
  },
  listSubscriptions: async (params?: {
    status?: string
    provider?: string
  }) => {
    const query = new URLSearchParams()
    if (params?.status) query.set('status', params.status)
    if (params?.provider) query.set('provider', params.provider)
    const qs = query.toString()
    const response = await api.get(
      `/billing/admin/subscriptions${qs ? `?${qs}` : ''}`
    )
    return response.data
  },
  getSubscription: async (tenantId: string) => {
    const response = await api.get(`/billing/admin/subscriptions/${tenantId}`)
    return response.data
  },
  overridePlan: async (tenantId: string, plan: string) => {
    const response = await api.post(
      `/billing/admin/subscriptions/${tenantId}/override-plan`,
      { plan }
    )
    return response.data
  },
  applyCredit: async (
    tenantId: string,
    amountCents: number,
    description: string
  ) => {
    const response = await api.post(
      `/billing/admin/subscriptions/${tenantId}/apply-credit`,
      {
        amountCents,
        description,
      }
    )
    return response.data
  },
}

// Pricing Plans API
export interface PricingPlan {
  id: string
  code: string
  name: string
  description: string | null
  conversationLimit: number
  maxLocations: number
  maxUsers: number
  priceMonthly: number | null
  priceYearly: number | null
  currency: string
  features: string[]
  verticalFeatures: Record<string, string[]> | null
  isActive: boolean
  isRecommended: boolean
  sortOrder: number
  rateLimitPerMinute: number
  rateLimitPerHour: number
  rateLimitBurst: number
  stripePriceIdMonthly: string | null
  stripePriceIdYearly: string | null
  createdAt: string
  updatedAt: string
  _count?: { Tenant: number }
}

export type CreatePricingPlanPayload = Omit<
  PricingPlan,
  'id' | 'createdAt' | 'updatedAt' | '_count'
>

export type UpdatePricingPlanPayload = Partial<CreatePricingPlanPayload>

export const pricingApi = {
  list: async (): Promise<PricingPlan[]> => {
    const response = await api.get('/pricing')
    return response.data
  },
  get: async (id: string): Promise<PricingPlan> => {
    const response = await api.get(`/pricing/${id}`)
    return response.data
  },
  create: async (payload: CreatePricingPlanPayload): Promise<PricingPlan> => {
    const response = await api.post('/pricing', payload)
    return response.data
  },
  update: async (
    id: string,
    payload: UpdatePricingPlanPayload
  ): Promise<PricingPlan> => {
    const response = await api.put(`/pricing/${id}`, payload)
    return response.data
  },
  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/pricing/${id}`)
    return response.data
  },
}

// Feature Management API
export interface Feature {
  id: string
  code: string
  name: string
  description: string | null
  category: string
  verticals: string[]
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export type CreateFeaturePayload = Omit<
  Feature,
  'id' | 'createdAt' | 'updatedAt'
>

export type UpdateFeaturePayload = Partial<CreateFeaturePayload>

export const featureApi = {
  list: async (): Promise<Feature[]> => {
    const response = await api.get('/features')
    return response.data
  },
  get: async (id: string): Promise<Feature> => {
    const response = await api.get(`/features/${id}`)
    return response.data
  },
  create: async (payload: CreateFeaturePayload): Promise<Feature> => {
    const response = await api.post('/features', payload)
    return response.data
  },
  update: async (
    id: string,
    payload: UpdateFeaturePayload
  ): Promise<Feature> => {
    const response = await api.put(`/features/${id}`, payload)
    return response.data
  },
  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/features/${id}`)
    return response.data
  },
}

// Security Dashboard API
export interface SecurityStats {
  auditEvents7d: number
  criticalHighEvents7d: number
  lockedAdmins: number
  activeSessions: number
  failedTenantLogins24h: number
}

export interface SecurityAuditEvent {
  id: string
  action: string
  resourceType: string
  resourceId: string | null
  severity: string
  ipAddress: string
  adminEmail: string | null
  adminName: string | null
  createdAt: string
  details: Record<string, unknown>
}

export interface AdminSecurityStatus {
  id: string
  email: string
  name: string
  role: string
  status: string
  mfaEnabled: boolean
  failedAttempts: number
  lockedUntil: string | null
  lastLoginAt: string | null
  lastLoginIp: string | null
  activeSessionCount: number
}

export interface SecurityDashboard {
  stats: SecurityStats
  recentCriticalEvents: SecurityAuditEvent[]
  adminStatus: AdminSecurityStatus[]
  generatedAt: string
}

// FEAT-141 — Login-attempt explorer (DIRECT) + runtime metrics (PROXY)

export interface LoginAttempt {
  id: string
  userId: string | null
  email: string
  success: boolean
  failureReason: string | null
  ipAddress: string | null
  userAgent: string | null
  location: string | null
  createdAt: string
}

export interface LoginAttemptsPage {
  attempts: LoginAttempt[]
  total: number
  page: number
  limit: number
}

export interface LoginAttemptsQuery {
  page?: number
  limit?: number
  email?: string
  ipAddress?: string
  success?: 'true' | 'false'
  startDate?: string
  endDate?: string
}

export interface KeyedCount {
  key: string
  count: number
}

export interface LoginAttemptStats {
  total: number
  successCount: number
  failureCount: number
  successRatio: number
  byFailureReason: { failureReason: string; count: number }[]
  topFailingIps: KeyedCount[]
  topFailingEmails: KeyedCount[]
  generatedAt: string
}

export interface RuntimeSecurityMetrics {
  failedLoginAttempts: number
  rateLimitViolations: number
  activeBruteForceAttempts: number
}

export const securityApi = {
  getDashboard: async (): Promise<SecurityDashboard> => {
    const response = await api.get('/security/dashboard')
    return response.data
  },
  getLoginAttempts: async (
    query: LoginAttemptsQuery = {}
  ): Promise<LoginAttemptsPage> => {
    const response = await api.get('/security/login-attempts', {
      params: query,
    })
    return response.data
  },
  getLoginAttemptStats: async (
    query: Omit<LoginAttemptsQuery, 'page' | 'limit'> = {}
  ): Promise<LoginAttemptStats> => {
    const response = await api.get('/security/login-attempts/stats', {
      params: query,
    })
    return response.data
  },
  getRuntimeMetrics: async (): Promise<RuntimeSecurityMetrics> => {
    const response = await api.get('/security/runtime-metrics')
    return response.data
  },
}

// Platform Settings API
export interface PlatformSettings {
  id: string
  platformName: string
  supportEmail: string
  defaultModel: string
  maxTokensPerRequest: number
  sessionTimeoutHours: number
  maxLoginAttempts: number
  enableNewTenantRegistration: boolean
  enableEmailNotifications: boolean
  maintenanceMode: boolean
  createdAt: string
  updatedAt: string
}

export type UpdatePlatformSettingsPayload = Partial<
  Omit<PlatformSettings, 'id' | 'createdAt' | 'updatedAt'>
>

export const platformSettingsApi = {
  get: async (): Promise<PlatformSettings> => {
    const response = await api.get('/settings')
    return response.data
  },
  update: async (
    payload: UpdatePlatformSettingsPayload
  ): Promise<PlatformSettings> => {
    const response = await api.put('/settings', payload)
    return response.data
  },
}

// Branding API
export const brandingApi = {
  get: async (tenantId: string) => {
    const response = await api.get(`/workspaces/${tenantId}/branding`)
    return response.data
  },
  update: async (tenantId: string, dto: Record<string, unknown>) => {
    const response = await api.put(`/workspaces/${tenantId}/branding`, dto)
    return response.data
  },
  uploadImage: async (
    tenantId: string,
    file: File,
    type: 'logo' | 'favicon'
  ) => {
    const form = new FormData()
    form.append('file', file)
    const response = await api.post(
      `/workspaces/${tenantId}/branding/logo?type=${type}`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    return response.data
  },
  reset: async (tenantId: string) => {
    const response = await api.delete(`/workspaces/${tenantId}/branding`)
    return response.data
  },
  resolveDomain: async (tenantId: string, domain: string) => {
    const response = await api.get(
      `/workspaces/${tenantId}/branding/resolve-domain`,
      {
        params: { domain },
      }
    )
    return response.data
  },
}

// Health API
export const healthApi = {
  system: async () => {
    const response = await api.get('/health/system')
    return response.data
  },
}

// MFA API (extension of auth)
export const mfaApi = {
  setup: async () => {
    const response = await api.post('/auth/mfa/setup')
    return response.data
  },
  verify: async (code: string) => {
    const response = await api.post('/auth/mfa/verify', { code })
    return response.data
  },
  disable: async (password: string) => {
    const response = await api.post('/auth/mfa/disable', { password })
    return response.data
  },
}

// Password change API
export const passwordApi = {
  change: async (currentPassword: string, newPassword: string) => {
    const response = await api.post('/auth/change-password', {
      currentPassword,
      newPassword,
    })
    return response.data
  },
}
